import { useCallback, useEffect, useRef } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';

import { RHFLocationSelect } from 'components/input/RHFLocationSelect';
import { Flex } from 'components/layout';
import {
  LTLAccessorial,
  QuickQuoteInputs,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { TMSLocation } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import {
  injectSelectedObject,
  normalizeExternalTMSID,
} from 'utils/loadInfoAndBuilding';

import {
  applyTaiAddressBookAccessorialsToForm,
  applyTaiAddressBookLocationToForm,
  clearTaiAddressBookAccessorialsFromForm,
  clearTaiAddressBookPrefillFromForm,
  isEmptyTaiCustomerScopeKey,
} from './applyTaiAddressBookLocation';

type TaiAddressBookSelectProps = {
  stopIndex: 0 | 1;
  locations: Maybe<TMSLocation[]>;
  setLocations: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
  isLoadingLocations: boolean;
  tmsID: Undef<number>;
  customerID: Undef<number>;
  customerExternalTmsId: Undef<string>;
  hasCustomer: boolean;
  accessorials: LTLAccessorial[];
  // When set from AI suggestion defaults, shows the AI-filled hint on Address book.
  aiHighlightedExternalTMSID?: string;
};

function isTaiAddressBookStub(
  location: TMSLocation,
  externalTMSID: string
): boolean {
  return (
    location.ID === 0 &&
    normalizeExternalTMSID(location.externalTMSID) === externalTMSID
  );
}

export function TaiAddressBookSelect({
  stopIndex,
  locations,
  setLocations,
  isLoadingLocations,
  tmsID,
  customerID,
  customerExternalTmsId,
  hasCustomer,
  accessorials,
  aiHighlightedExternalTMSID,
}: TaiAddressBookSelectProps) {
  const { control, setValue, getValues } = useFormContext<QuickQuoteInputs>();
  const { errors } = useFormState({ control });
  const externalTMSID = useWatch({
    control,
    name: `stops.${stopIndex}.externalTMSID`,
  });
  const lastAppliedExternalIdRef = useRef('');
  // Favorite whose accessorials were applied with a loaded catalog. Without the
  // catalog every accessorial id is dropped, so they are applied again once it loads.
  const accessorialsAppliedExternalIdRef = useRef('');
  // Placeholder row for an AI-seeded id missing from the list. It only makes the
  // selection visible; it carries no accessorials, so it is never applied.
  const injectedStubRef = useRef<Maybe<TMSLocation>>(null);
  const customerScopeKey = `${customerID ?? ''}:${String(customerExternalTmsId ?? '').trim()}`;
  const prevCustomerScopeKeyRef = useRef(customerScopeKey);

  // Accessorials of the favorite currently selected on the other stop, so clearing
  // this stop keeps the ones that favorite also needs.
  const getOtherStopFavoriteAccessorialIds = useCallback((): string[] => {
    const otherId = normalizeExternalTMSID(
      getValues(`stops.${stopIndex === 0 ? 1 : 0}.externalTMSID`)
    );
    if (!otherId) {
      return [];
    }
    const favorite = (locations ?? []).find(
      (loc) => normalizeExternalTMSID(loc.externalTMSID) === otherId
    );
    return (favorite?.accessorials ?? []).map(String);
  }, [getValues, locations, stopIndex]);

  // On customer switch (A → B), drop prior favorite selection + prefill
  // only when Address book was used. Free-form / AI stop data is kept.
  // First customer pick (empty → A) also keeps free-form / AI stop data.
  useEffect(() => {
    if (prevCustomerScopeKeyRef.current === customerScopeKey) {
      return;
    }
    const previousScopeKey = prevCustomerScopeKeyRef.current;
    prevCustomerScopeKeyRef.current = customerScopeKey;

    if (isEmptyTaiCustomerScopeKey(previousScopeKey)) {
      return;
    }

    const injectedStub = injectedStubRef.current;
    injectedStubRef.current = null;
    if (injectedStub) {
      setLocations(
        (prev) => prev?.filter((location) => location !== injectedStub) ?? prev
      );
    }

    const hadAddressBookSelection = Boolean(
      normalizeExternalTMSID(getValues(`stops.${stopIndex}.externalTMSID`))
    );
    lastAppliedExternalIdRef.current = '';
    accessorialsAppliedExternalIdRef.current = '';
    // Both stops' favorites belong to the previous customer, so nothing is kept.
    clearTaiAddressBookAccessorialsFromForm(stopIndex, getValues, setValue);
    if (!hadAddressBookSelection) {
      return;
    }

    clearTaiAddressBookPrefillFromForm(stopIndex, setValue);
  }, [customerScopeKey, getValues, setLocations, setValue, stopIndex]);

  // If AI seeded an externalTMSID that is not in the loaded favorites (yet),
  // inject a placeholder from the form stop so the select shows the selection.
  useEffect(() => {
    const id = normalizeExternalTMSID(externalTMSID);
    if (!id || locations == null) {
      const injectedStub = injectedStubRef.current;
      injectedStubRef.current = null;
      if (injectedStub) {
        setLocations(
          (prev) =>
            prev?.filter((location) => location !== injectedStub) ?? prev
        );
      }
      return;
    }
    const alreadyListed = locations.some(
      (loc) => normalizeExternalTMSID(loc.externalTMSID) === id
    );
    if (alreadyListed) {
      return;
    }

    const stop = getValues(`stops.${stopIndex}`);
    const stub: TMSLocation = {
      ID: 0,
      apptRequired: false,
      externalTMSID: id,
      name: stop?.addressName ?? '',
      addressLine1: stop?.addressLine1 ?? '',
      addressLine2: stop?.addressLine2 ?? '',
      city: stop?.city ?? '',
      state: stop?.state ?? '',
      zipCode: stop?.zip ?? '',
      country: stop?.country ?? '',
      contact: '',
      phone: '',
      email: '',
      accessorials: [],
    };
    injectedStubRef.current = stub;
    setLocations((prev) => injectSelectedObject(stub, prev ?? []));
  }, [externalTMSID, getValues, locations, setLocations, stopIndex]);

  useEffect(() => {
    const id = normalizeExternalTMSID(externalTMSID);
    if (!id) {
      // Rep cleared the Address book: drop the cleared favorite's accessorials.
      if (lastAppliedExternalIdRef.current) {
        lastAppliedExternalIdRef.current = '';
        accessorialsAppliedExternalIdRef.current = '';
        clearTaiAddressBookAccessorialsFromForm(
          stopIndex,
          getValues,
          setValue,
          getOtherStopFavoriteAccessorialIds(),
          accessorials
        );
      }
      return;
    }
    if (id === lastAppliedExternalIdRef.current) {
      // Address already applied: add the accessorials once the catalog has loaded.
      if (
        id !== accessorialsAppliedExternalIdRef.current &&
        accessorials.length > 0
      ) {
        const applied = (locations ?? []).find(
          (loc) => normalizeExternalTMSID(loc.externalTMSID) === id
        );
        if (applied && applied !== injectedStubRef.current) {
          accessorialsAppliedExternalIdRef.current = id;
          applyTaiAddressBookAccessorialsToForm(
            stopIndex,
            applied,
            getValues,
            setValue,
            accessorials,
            getOtherStopFavoriteAccessorialIds()
          );
        }
      }
      return;
    }

    if (
      normalizeExternalTMSID(getValues(`stops.${stopIndex}.externalTMSID`)) !==
      id
    ) {
      return;
    }

    const selected = (locations ?? []).find(
      (loc) => normalizeExternalTMSID(loc.externalTMSID) === id
    );
    // Wait for the real favorite; applying the placeholder would mark the id as
    // applied and skip the favorite's accessorials once the list loads.
    if (
      !selected ||
      selected === injectedStubRef.current ||
      isTaiAddressBookStub(selected, id)
    ) {
      return;
    }

    const injectedStub = injectedStubRef.current;
    injectedStubRef.current = null;
    if (injectedStub) {
      setLocations(
        (prev) => prev?.filter((location) => location !== injectedStub) ?? prev
      );
    }
    lastAppliedExternalIdRef.current = id;
    accessorialsAppliedExternalIdRef.current =
      accessorials.length > 0 ? id : '';
    applyTaiAddressBookLocationToForm(
      stopIndex,
      selected,
      getValues,
      setValue,
      accessorials,
      getOtherStopFavoriteAccessorialIds()
    );
  }, [
    accessorials,
    externalTMSID,
    getOtherStopFavoriteAccessorialIds,
    getValues,
    locations,
    setLocations,
    setValue,
    stopIndex,
  ]);

  return (
    <Flex direction='col' gap='xs' className='w-full mx-0 mb-2'>
      <RHFLocationSelect<QuickQuoteInputs>
        name={`stops.${stopIndex}.externalTMSID`}
        label='Address book'
        control={control}
        errors={errors}
        locations={locations}
        setLocations={setLocations}
        isLoadingLocations={isLoadingLocations}
        labelClassName='text-sm font-normal text-neutral-800'
        required={false}
        disabled={!hasCustomer || !tmsID}
        placeholder={
          hasCustomer
            ? 'Search saved locations'
            : 'Select a customer to load address book'
        }
        tmsID={tmsID}
        customerID={customerID}
        customerExternalTmsId={customerExternalTmsId}
        labelIsName
        aiHighlightedExternalTMSID={aiHighlightedExternalTMSID}
      />
    </Flex>
  );
}
